import React, { useEffect, useState } from 'react';
import API from '../services/api';

// Billing vs Calendar reconciliation: compares daily_logs submissions against
// Acuity appointments per therapist per day, highlighting kept appointments
// with no submission (missed) and submissions with no appointment (off-calendar).

function ymd(d) {
  return d.toISOString().split('T')[0];
}
function fmtDay(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}
function fmtMoney(v) {
  return `$${parseFloat(v || 0).toFixed(2)}`;
}

const cardStyle = {
  background: 'var(--white)', border: '1px solid var(--border-color)',
  borderRadius: 10, padding: '1rem 1.25rem', marginBottom: 14,
};
const statStyle = { textAlign: 'center', minWidth: 110 };
const statValStyle = { fontSize: 22, fontWeight: 700 };
const statLblStyle = { fontSize: 12, color: 'var(--text-muted)' };
const rowStyle = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
  padding: '6px 8px', borderRadius: 6, fontSize: 13.5, alignItems: 'center',
};
const inputStyle = { padding: '7px 9px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 14 };

function AdminBillingReconciliationPage() {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 6 * 86400000);
  const [startDate, setStartDate] = useState(ymd(weekAgo));
  const [endDate, setEndDate] = useState(ymd(today));
  const [therapistId, setTherapistId] = useState('');
  const [therapists, setTherapists] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get('/admin/users')
      .then((res) => setTherapists((res.data || []).filter((u) => u.role === 'THERAPIST')))
      .catch(() => {});
  }, []);

  const run = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { start: startDate, end: endDate };
      if (therapistId) params.therapistId = therapistId;
      const res = await API.get('/admin/billing-reconciliation', { params });
      setData(res.data);
    } catch (err) {
      console.error('Reconciliation error:', err);
      setError(err.response?.data?.message || 'Error running reconciliation.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Run once on load with the default week.
  useEffect(() => { run(); }, []);

  const totals = data?.totals;

  return (
    <div style={{ margin: '2rem', maxWidth: 1100 }}>
      <h2 style={{ marginBottom: 4 }}>Billing vs Calendar</h2>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
        Compares each therapist's Acuity calendar against their submitted sessions
        and flags kept appointments that were never billed. Calendar data starts{' '}
        {data?.dataSince || '2026-06-01'}; client-name matching is approximate, so
        eyeball anything flagged before following up.
      </p>

      <div style={{ ...cardStyle, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={statLblStyle}>Start date</div>
          <input type="date" style={inputStyle} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <div style={statLblStyle}>End date</div>
          <input type="date" style={inputStyle} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div>
          <div style={statLblStyle}>Therapist</div>
          <select style={inputStyle} value={therapistId} onChange={(e) => setTherapistId(e.target.value)}>
            <option value="">All therapists</option>
            {therapists.map((t) => (
              <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={run} disabled={loading}
          style={{
            background: 'var(--primary-color)', color: '#fff', border: 'none',
            borderRadius: 6, padding: '9px 18px', fontWeight: 700, cursor: 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Checking…' : 'Run Check'}
        </button>
      </div>

      {error && <div style={{ color: 'var(--error-color)', marginBottom: 12 }}>{error}</div>}

      {totals && (
        <div style={{ ...cardStyle, display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'space-around' }}>
          <div style={statStyle}>
            <div style={statValStyle}>{totals.appointments}</div>
            <div style={statLblStyle}>Kept appointments</div>
          </div>
          <div style={statStyle}>
            <div style={statValStyle}>{totals.submissions}</div>
            <div style={statLblStyle}>Submissions</div>
          </div>
          <div style={statStyle}>
            <div style={{ ...statValStyle, color: 'var(--success-color)' }}>{totals.matched}</div>
            <div style={statLblStyle}>Matched</div>
          </div>
          <div style={statStyle}>
            <div style={{ ...statValStyle, color: totals.missed > 0 ? 'var(--error-color)' : 'var(--success-color)' }}>
              {totals.missed}
            </div>
            <div style={statLblStyle}>🔴 Missed submissions</div>
          </div>
          <div style={statStyle}>
            <div style={{ ...statValStyle, color: totals.offCalendar > 0 ? '#b45309' : 'var(--success-color)' }}>
              {totals.offCalendar}
            </div>
            <div style={statLblStyle}>🟡 Off-calendar</div>
          </div>
        </div>
      )}

      {data && data.days.length === 0 && !loading && (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
          Nothing on the calendar or billed in this range.
        </div>
      )}

      {data && data.days.map((day) => {
        const clean = day.missed.length === 0 && day.offCalendar.length === 0;
        return (
          <div key={`${day.date}-${day.therapistId}`} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <strong>{day.therapistName} — {fmtDay(day.date)}</strong>
              <span style={{ fontSize: 13, color: clean ? 'var(--success-color)' : 'var(--text-muted)', fontWeight: 600 }}>
                {clean ? '✅ All accounted for' : `${day.missed.length} missed · ${day.offCalendar.length} off-calendar`}
                {day.canceledCount > 0 && (
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {day.canceledCount} canceled appt{day.canceledCount > 1 ? 's' : ''}</span>
                )}
              </span>
            </div>

            <div style={{ ...rowStyle, fontWeight: 700, fontSize: 12, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
              <div>ON THE CALENDAR</div>
              <div>SUBMITTED</div>
            </div>

            {day.matched.map((m, i) => (
              <div key={`m-${i}`} style={{ ...rowStyle, background: i % 2 ? 'var(--surface-muted)' : 'transparent' }}>
                <div>🕐 {m.appt.time} — <strong>{m.appt.clientName}</strong>
                  <span style={{ color: 'var(--text-muted)' }}> · {m.appt.type}</span>
                </div>
                <div>✅ <strong>{m.log.clientName}</strong>
                  <span style={{ color: 'var(--text-muted)' }}> · {m.log.serviceType} · {fmtMoney(m.log.billed)} · {m.log.status}</span>
                </div>
              </div>
            ))}

            {day.missed.map((a, i) => (
              <div key={`x-${i}`} style={{ ...rowStyle, background: '#fde8e8', border: '1px solid #f5c2c2' }}>
                <div>🕐 {a.time} — <strong>{a.clientName}</strong>
                  <span style={{ color: 'var(--text-muted)' }}> · {a.type}</span>
                </div>
                <div style={{ color: 'var(--error-color)', fontWeight: 700 }}>❌ No submission found</div>
              </div>
            ))}

            {day.offCalendar.map((l, i) => (
              <div key={`o-${i}`} style={{ ...rowStyle, background: '#fff3cd', border: '1px solid #ffe69c' }}>
                <div style={{ color: '#664d03', fontWeight: 600 }}>— Not on calendar</div>
                <div>🟡 <strong>{l.clientName}</strong>
                  <span style={{ color: 'var(--text-muted)' }}> · {l.serviceType} · {fmtMoney(l.billed)} · {l.status}</span>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default AdminBillingReconciliationPage;
